import type { ApiObject, ApiOptions } from "@rudderstack/analytics-js";

import type {
  issue__AlternativePaymentOption as AlternativeStrategy,
  AuthenticateResultErrorType,
  InsuranceStatus,
  issue__PaymentAgreementContract,
  issue__PaymentCaseType,
  issue__ProviderRelationship,
  issue__UserClosureReason,
  NateraIntakeActivationItem,
} from "@medbillai/generated-graphql-types";

import type { GetHelpPromptKey } from "@medbillai/schemas";
import type { DocumentScanEntryPoint } from "@medbillai/utils/documentScan";
import type {
  UploadMethod,
  UploadPlatform,
} from "@medbillai/utils/documentUpload";
import type {
  DocumentRequestCardVariant,
  IntakeRoutingNateraDestination,
  IntakeRoutingVariant,
  NateraExpressVariant,
  NateraIntakePlaybookVariant,
  NateraIntakeStoryVariant,
  OnboardingBillCompletionDestination,
} from "@medbillai/utils/flag";
import type {
  UserInputAnswerMode,
  UserInputTextShape,
} from "@medbillai/utils/userInput";

import type { CaseListFilters } from "../../screens/cases/utils/types";
import type { FAWizardOpenSource } from "../../screens/fa-wizard/FAApplicationProvider";
import type { InsuranceLinkStep } from "../accounts-link/types";
import type { InAppBrowser } from "../inAppBrowser";
import type { SingularEventName } from "../singular/types";

// Append-only: events already emitted keep the version they were tagged with,
// so this union outlives `ONBOARDING_VERSIONS`, which tracks only servable arms.
export type OnboardingVersion = "v2.0" | "v3.0";
// Partner-invited runs suffix the partner slug (e.g. "v3.0-acme") so the plain
// values stay groupable.
export type OnboardingVersionTag =
  | OnboardingVersion
  | `${OnboardingVersion}-${string}`;
export const onboardingVersionTag = (
  version: OnboardingVersion,
  partnerSlug?: string,
): OnboardingVersionTag =>
  partnerSlug ? `${version}-${partnerSlug}` : version;
export type PanelSource =
  | "nav_sidebar"
  | "case_header"
  | "overlay_dismiss"
  | "home_your_cases";
export type PanelToggleAction = "open" | "close";
export type CaseAnalyticsSource = "your-cases-home" | "attention-alert";
export type InviteActivationFailureReason = "missing_token" | "unresolved";

export type CaseListFilterType = "open" | "unread" | "read" | "closed";

export const defaultSessionReplaySampleRate = 0.3;

export type AnalyticsConfig = {
  writeKey: string;
  dataPlaneUrl: string;
  // when running in debug mode, events will be logged to the console
  debug?: boolean;
  // Amplitude API key for session replay
  amplitudeApiKey?: string;
  // Session replay configuration
  sessionReplay?: {
    sampleRate?: number; // Percentage of sessions to capture (0-1)
    enabled?: boolean;
  };
};

export const defaultConfig: ApiOptions = {
  integrations: {
    All: true,
    "Facebook Conversions": false,
    "Google Ads Enhanced conversions Mobile App": false,
  },
};

export const trackingConfig: ApiOptions = {
  integrations: {
    All: true,
    "Facebook Conversions": true,
    // c is lower case to match "Display Name"
    "Google Ads Enhanced conversions Mobile App": true,
  },
};

type Endpoint = "WEB_AGENT";

type FakeDoorMmfaEventBase = {
  linkCategory?: "INSURANCE" | "EHR";
  variant: "connect_existing" | "create_email";
};
export type InsuranceLinkPage =
  | "RELINK"
  | "LOGIN"
  | "MFA_CODE"
  | "SECURITY_QUESTION"
  | "SELECT_MFA"
  | "MANAGED_MFA_APPROVAL"
  | "SUCCESS"
  | "ERROR";

export enum LogoutReason {
  USER_LOGOUT = "user_logout",
  UNAUTHORIZED = "unauthorized",
  AUTH_ERROR = "auth_error",
  FORCED_LOGOUT = "forced_logout",
  SHADOW_LOGOUT = "shadow_logout",
  ACCOUNT_DELETION = "account_deletion",
  UNKNOWN = "unknown",
}

/** Steps in the BillPayAgreementModal wizard flow. */
export type BillPayStepId =
  | "howItWorks"
  | "savingsPreference"
  | "alternativeStrategies"
  | "successFee";

// Shared shape for every Bill Pay v2 funnel event. `eventId` is the
// BillPayRequestedEvent id, joinable to the domain event in Hex.
type BillPayEventBase = { issueId: string; eventId: string };

/**
 * Shared shape for Scanbot document-scan funnel events. `scanSessionId` is
 * generated per scanner open; `issueId` is present when scanning from a case.
 */
export type DocumentScanEventBase = {
  scanSessionId: string;
  issueId?: string;
  entryPoint: DocumentScanEntryPoint;
  /** Joins the scan sub-funnel to the upload funnel attempt that launched it. */
  uploadSessionId?: string;
};

/**
 * Arguments for opening the native document scanner. The acknowledge-screen
 * quality gate is not a caller concern — `openScanbot` evaluates the quality
 * experiment itself at launch.
 */
export type DocumentScanOpenParams = {
  entryPoint: DocumentScanEntryPoint;
  issueId?: string;
  /**
   * The upload funnel attempt this scan belongs to. Lets the `DocumentScan*`
   * events (which keep their own `scanSessionId`) join to the surrounding
   * sheet-open → submit → outcome funnel without a cross-schema join.
   */
  uploadSessionId?: string;
};

/**
 * Shared shape for every upload-funnel event. `uploadSessionId` is minted when
 * the upload sheet opens and threads through every subsequent step, so
 * open → method → picker-return → submit → outcome is one joinable chain —
 * the same trick that makes the scanner funnel analyzable, extended to the
 * gallery and file pickers that carry most of the volume.
 */
export type UploadFunnelEventBase = {
  uploadSessionId: string;
  entryPoint: DocumentScanEntryPoint;
  platform: UploadPlatform;
  issueId?: string;
};

/**
 * How one picker open ended. `NO_RESPONSE` is emitted on a timeout rather than
 * a real callback, and is ambiguous on its own: the web `expo-*-picker` shims
 * may never report a cancellation, so an ordinary backed-out dialog and a file
 * dialog the browser refused to open (synthetic click without user activation,
 * which hangs the sheet spinner forever) both look like a promise that never
 * settles. The disambiguator is `errorType: ACTIVATION_LOST`, set when the
 * browser reported no transient activation at the moment the picker opened —
 * that subset is the measured hang; the remainder is a ceiling that includes
 * unreported cancels.
 */
export type UploadPickerOutcome =
  | "SELECTED"
  | "CANCELED"
  | "ERROR"
  | "NO_RESPONSE"
  // Web only: a newer trigger tap opened a replacing dialog before this one
  // answered. Not a hang and not a cancel, but the attempt still has to
  // terminate — every attempt that reported a method reaches exactly one
  // terminal, and a `SUPERSEDED` counted as `NO_RESPONSE` would inflate the
  // hang measurement it exists to size.
  | "SUPERSEDED";

/**
 * Which leg of the upload failed. Absent for failures that never reached the
 * network (an archive rejected by the client-side guard, a picker error).
 */
export type UploadFailureStage = "CREATE" | "S3_PUT" | "FINALIZE";

/**
 * Normalized failure cause. `stage` alone is too coarse to act on — it can't
 * separate an archive rejection from a GraphQL failure, or S3 retries running
 * out (`S3_TRANSIENT_EXHAUSTED`, a capacity signal) from a hard rejection
 * (`S3_FAILED`, a permissions or payload signal). Raw error messages go to
 * Sentry, not Rudder.
 */
export type UploadErrorType =
  | "PICKER_PERMISSION_DENIED"
  | "PICKER_ERROR"
  | "ACTIVATION_LOST"
  | "BLOB_CONVERSION_FAILED"
  | "UNSUPPORTED_FILE_TYPE"
  | "ARCHIVE_REJECTED"
  | "BUNDLED_SELECTION_REJECTED"
  | "CREATE_FAILED"
  | "S3_TRANSIENT_EXHAUSTED"
  | "S3_FAILED"
  | "FINALIZE_FAILED";

/**
 * Radio the upload finished on. Distinguishes a failure on poor cellular from
 * a flaky bucket — `errorType` alone cannot. Omitted when the platform cannot
 * tell (Safari/Firefox have no Network Information API).
 */
export type UploadConnectionType = "wifi" | "cellular" | "offline";

/**
 * Why a scan session ended without a Completed/Canceled/Failed event.
 * `BACKGROUNDED`: the app was sent to the background long enough that the
 * user is unlikely to return and finish. `TERMINATED`: a session still marked
 * active was discovered at the next app launch (force-quit, crash, OS-kill).
 */
export type DocumentScanAbandonReason = "BACKGROUNDED" | "TERMINATED";

/**
 * Why an in-flight upload never reported an outcome. Both are discovered at the
 * next app launch from a durable record — an upload that dies takes its JS with
 * it, so there is no in-process moment to emit from. `BACKGROUNDED`: the app had
 * left the foreground while this upload was in flight, so the OS reclaiming it
 * is the likely cause. `TERMINATED`: it died in the foreground (crash, OS-kill
 * under memory pressure, force-quit from the app switcher).
 */
export type UploadAbandonReason = "BACKGROUNDED" | "TERMINATED";

/** Document Quality Analyzer ratings from Scanbot (plus UNKNOWN). */
export type DocumentScanQuality =
  | "VERY_POOR"
  | "POOR"
  | "REASONABLE"
  | "GOOD"
  | "EXCELLENT"
  | "UNKNOWN";

// Shared shape for every payment-preferences funnel event. `eventId` is the
// PaymentPreferencesRequestedEvent id, joinable to the domain event.
type PaymentPrefsEventBase = {
  issueId: string;
  eventId: string;
  contract: issue__PaymentAgreementContract;
};

// Why the question path was offered. The card withholds it on first render, so
// this separates the two members it is meant to reach: one who opened the flow
// and backed out, and one who never opened it at all.
export type PaymentPrefsQuestionsRevealReason = "flow_abandoned" | "card_idle";

// The entry point that launched the takeover.
export type PaymentPrefsSource =
  | "event_card"
  | "post_escalation"
  | "natera_intake";

// Shared shape for the FA intake-wizard funnel. `issueId` is the application's
// join key (one FA application per case). Per-session grouping (time-on-form,
// steps-per-session) rides on RudderStack's auto session id, already attached to
// every event's context — no bespoke per-open id.
type FinancialAssistanceWizardEventBase = {
  issueId: string;
};

// Flow-based event naming convention (onboarding today, FA next):
// `<Prefix><ScreenTag>StepShown` fires on every state entry and
// `<Prefix><FromScreenTag><OutcomeTag>Outcome` on every outcome, because
// Amplitude User Journeys can't read event properties — the tag must be in
// the name. Back and plain-continue funnel into the shared
// `<Prefix>BackOutcome` / `<Prefix>ContinueOutcome` (the screen rides in
// `fromScreenTag`). Shells fire these through `createFlowTracker`
// (./flowEvents.ts) from explicit `match(...).exhaustive()` blocks (screens
// from `onStateLoaded`, outcomes from `onTransition`), so adding a
// screen or outcome to a flow config is a build error until the shell arm
// and its entry here exist. The tracker owns the envelope; each arm
// hand-picks any extra properties as a fresh literal typed from the entry —
// never spread flow data into a payload; the spread would bypass
// excess-property checking and ship undeclared fields.
type OnboardingScreenEventBase = {
  screenTag: string;
  onboardingVersion: OnboardingVersionTag;
  // Flow-state fields any screen event may carry; onboarding data is
  // deliberately all trackable, so these live on the base.
  intent?: string;
  householdSize?: number;
};

type OnboardingOutcomeEventBase = {
  outcomeTag: string;
  fromScreenTag: string;
  onboardingVersion: OnboardingVersionTag;
};

/** The funnel build, so an R-vs-S ablation reads without re-instrumenting. */
export type NateraIntakeVersionTag =
  | "natera-r1"
  | "natera-r2"
  | "natera-r3"
  | "natera-r4"
  | "natera-r5"
  | "natera-r6"
  | "natera-r7"
  | "natera-r8"
  | "natera-r10"
  | "natera-r11"
  | "natera-r12";

/** Sort on this, not the tag (`natera-r10` < `natera-r9` as strings). Null
 *  on rows before r4 — read it as lower than any revision. */
type NateraIntakeRevision = { funnelRevision: number };

/** Which surface walked the member through activation. */
export type NateraIntakeSurface = "hub" | "chat";

/** Carried by every activation event a case can reach on either surface, so the
 *  chat is judged against the hub's own numbers: one funnel query, split by one
 *  column. Absent on the identity and phone steps, which run before a case
 *  exists and so before either surface is chosen. */
type NateraIntakeSurfaced = { surface: NateraIntakeSurface };

/** Raw arm, twins intact. */
type NateraIntakeExpressArmed = { expressArm?: NateraExpressVariant };

/** Mirrors `FaRead["kind"]` — the funnel's biggest branch, and its likeliest
 *  drop-off driver. A drift in the read's kinds is a build error at the
 *  tracker, which is why this is enumerated rather than widened to `string`. */
export type NateraIntakeFaBranch =
  | "likely-qualifies"
  | "unlikely-to-qualify"
  | "negotiation-unavailable"
  | "fa-not-pursued";

/** Why a member landed on an activation error screen. Only `caseCreationFailed`
 *  has a companion failure event; the rest are silent short of this one. */
export type NateraIntakeActivationErrorReason =
  | "caseCreationFailed"
  | "sessionClearFailed"
  /** Post-sign-up answers failed to resume or save. */
  | "postAuthAnswersFailed"
  /**
   * Any activation call failed, not only the first read: the screen is also
   * where a failed post-upload refresh and a failed Submit land.
   */
  | "activationRequestFailed"
  | "agreementReadFailed"
  /** The read settled with no agreement at all — a case opened without one. */
  | "agreementMissing";

// Natera partner intake funnel — same StepShown/Outcome convention as
// onboarding above. `persona` and `faBranch` are absent until the screens that
// assign them (Feeling / Analyzing) and present on everything after, so
// "did persona X drop off harder at step N" needs no self-join back to the
// assigning event.
type NateraIntakeEventBase = NateraIntakeRevision &
  NateraIntakeExpressArmed & {
    funnelVersion: NateraIntakeVersionTag;
    /** Position on the funnel's main path; 0 for the off-path exit screen. */
    stepIndex: number;
    persona?: string;
    faBranch?: NateraIntakeFaBranch;
    /** Raw arm, experiment twins intact; absent before the fork or when unenrolled. */
    playbookArm?: NateraIntakePlaybookVariant;
    /** Raw arm, experiment twins intact; absent before the fork or when unenrolled. */
    storyArm?: NateraIntakeStoryVariant;
    /** Where the hero questions were answered; absent on pre-r8 sessions. */
    landingEntry?: "offsite" | "onsite" | "routing";
  };

type NateraIntakeScreenEventBase = NateraIntakeEventBase & {
  screenTag: string;
};

type NateraIntakeOutcomeEventBase = NateraIntakeEventBase & {
  outcomeTag: string;
  fromScreenTag: string;
};

/** Why the eligibility check's plan card stayed off the express quote. */
export type NateraIntakeQuoteInsuranceMiss =
  | "payerUninsured"
  | "notStarted"
  | "needsInfo"
  | "checkFailed"
  | "unknownState"
  | "noActiveMembership"
  | "staleCoverage"
  | "noDetails";

export type NateraIntakeQuoteInsuranceOutcome =
  | "shown"
  /** No listed payer: uninsured, skipped, or a typed-in carrier, so no check was read. */
  | "noPayer"
  /** Still checking when the plan wait ended and the quote was decided. */
  | "timedOut"
  /** The user left between the payer pick and the quote's decision. */
  | "leftEarly"
  /** Came back partway through, so this session never asked for the payer. */
  | "resumed"
  /** The card was in, but Natera won't negotiate with this carrier, so it wasn't shown. */
  | "notNegotiable"
  | NateraIntakeQuoteInsuranceMiss;

type IntakeRoutingScreenEventBase = {
  screenTag: string;
};

type IntakeRoutingOutcomeEventBase = {
  outcomeTag: string;
  fromScreenTag: string;
};

export type CardCollectionWallet = "apple_pay" | "google_pay";

// `wallet` is set when the capture went through Apple Pay / Google Pay.
type PaymentPrefsWalletEventBase = PaymentPrefsEventBase & {
  wallet?: CardCollectionWallet;
};

export type CardCollectionMethod = CardCollectionWallet | "card";

/** A step of saving a card from wallet pay or the card sheet. */
export type CardSaveStep =
  | "started"
  | "cancelled"
  | "failed"
  | "saveTapped"
  | "succeeded";

export type EventTypes = {
  // Application lifecycle
  ClientVersionMismatch: {
    clientBinary: "OLD" | "NEW";
    clientVersion: string;
    apiVersion: string;
  };
  StartedEASUpdate: {
    clientVersion: string;
    easUpdateId: string;
    isLatestVersion: boolean;
  };
  EASUpdateMismatch: {
    clientVersion: string;
    supportedUpdateId: string | null;
    currentUpdateId: string | null;
    availableUpdateId: string | null;
    isLatestVersion: boolean;
  };
  EASUpdateNavigating: {
    authIsReady: boolean;
  };
  EASUpdateNavigated: {
    directive: "TIMEOUT" | "DIRECT";
  };
  NoUpdateAvailable: {
    clientVersion: string;
    latestVersion: string;
  };
  // SubmitLogin is used to track the method of login used by the user, after
  // initiating either an email or oauth login. Every surface that sends a login
  // email fires it.
  SubmitLogin: {
    method: "EMAIL" | "OAUTH";
    /**
     * Which email we actually sent, for method="EMAIL". The denominator for
     * the OTP funnel: without it a send is indistinguishable between the
     * methods, and only arrivals (EmailOtpCodeSubmitted) would be countable.
     * `CROSS_BROWSER_LINK` is a Meta in-app browser's send, a link to open in
     * the member's real browser; it is outside the OTP funnel.
     */
    deliveryMethod?: "MAGIC_LINK" | "EMAIL_OTP" | "CROSS_BROWSER_LINK";
    /** The Meta in-app browser the login started in; `none` on native. */
    inAppBrowser: InAppBrowser;
  };
  FaceIdLogoutButton: Record<string, never>;
  FaceIdVerifyButton: Record<string, never>;
  // Verification on the authentication screen for a magic link login
  EmailVerificationLinkError: {
    type:
      | "INVALID_PKCE_CODE_CHALLENGE"
      | "MISSING_TOKEN"
      | "MISSING_EMAIL"
      | "UNABLE_TO_AUTH_MAGIC_LINK"
      | "INVALID_EMAIL_OTP"
      | "UNKNOWN_ERROR";
  };
  /**
   * 6-digit code submitted on the OTP entry screen (typed or via the
   * code-carrying email link's pre-filled Continue).
   *
   * The OTP funnel reads: `SubmitLogin{deliveryMethod:EMAIL_OTP}` sent →
   * `EmailOtpCodeSubmitted{attempt:1}` reached the screen and tried →
   * `EmailVerificationLinkError{type:INVALID_EMAIL_OTP}` rejected. Successes
   * are the difference; a per-code retry curve is `attempt`, and
   * `EmailOtpCodeResent` is the deliverability signal.
   */
  EmailOtpCodeSubmitted: {
    /** 1-based try count for the code in hand; a resend restarts it. */
    attempt: number;
    /** Pre-filled by the emailed link rather than typed from the email. */
    fromLink: boolean;
    /** `navigator.webdriver`: the browser admits to being automated. */
    webdriver: boolean;
    /**
     * Time from the screen opening to this submit. Scanners that follow the
     * link and press Continue do it within about a second; a person is slower.
     */
    msSinceOpen: number;
  };
  // A new code was requested from the OTP entry screen. Climbing resend rate
  // is how slow or undelivered OTP email shows up.
  EmailOtpCodeResent: Record<string, never>;
  /**
   * A submitted code authenticated. Mirrors `EmailOtpCodeSubmitted` so the
   * funnel closes on the same cuts: `attempt` is the retry curve to success,
   * `fromLink` splits the code-carrying link from a typed code.
   *
   * Emitted rather than inferred. Success used to be read off
   * `OnboardingFlowStepForward{from:"otp_entry"}`, which is a routing event
   * that a refactor of the auth callback can move or drop without anyone
   * noticing the metric went with it.
   */
  EmailOtpCodeAccepted: {
    /** 1-based try count for the code that worked; a resend restarts it. */
    attempt: number;
    /** Pre-filled by the emailed link rather than typed from the email. */
    fromLink: boolean;
    webdriver: boolean;
    msSinceOpen: number;
  };
  /**
   * Any token-bearing link landed on the authenticate screen, before anything
   * is redeemed — `method` is the `stytch_token_type`, and most are `oauth`, so
   * filter to `magic_links`/`email_otp` for emailed links. Email security
   * scanners follow those too. `webdriver` true proves automation; false proves
   * nothing, since scanners hide it. Split on `msSinceOpen` of the submit that
   * follows: scanners cluster at a fixed delay after load, people spread out.
   */
  LoginLinkOpened: { method: string; webdriver: boolean };
  /**
   * A login email we tried to send and did not. `SubmitLogin` only fires once
   * a send succeeds, so without this a Stytch outage, a bot-control dead end,
   * or a hard-failing OTP send reads as *fewer sends* rather than as failures.
   *
   * `deliveryMethod` is set only where the caller knows it — the login screen
   * asks for an email and the server picks the method, so a failure there has
   * none to report. That stays attributable because `email-otp-login` is a
   * server-side kill switch on one shared context: every send in a window
   * uses the same method, so the window supplies what the event can't.
   */
  LoginEmailSendFailed: {
    reason: "BOT_CONTROL" | "FORM_ERROR" | "EXCEPTION";
    deliveryMethod?: "MAGIC_LINK" | "EMAIL_OTP";
  };
  // Verification on the authentication screen for an OAuth login
  OAuthVerificationError: {
    type: "INVALID_AUTH_OAUTH" | "UNKNOWN_ERROR";
  };
  AuthenticateError: {
    type: AuthenticateResultErrorType;
  };
  ClickedTab: { tabName: string };
  ClickedSettings: Record<string, never>;
  OnboardingBackStep: { initialRoute: string };
  // Called any time we redirect to the login screen with a forceLogout param
  // The reason depends on the context of the redirect. We set it to shadow_logout
  // when we log out from shadow user mode so that can be used to filter out logouts
  // from shadow users.
  Logout: { reason: LogoutReason };

  // Notifications
  // Called when we initiate the permission prompt
  ClickedEnableNotifications: Record<string, never>;
  // Called when the user has already denied notifications
  ClickedEnableNotificationsAlreadyDenied: Record<string, never>;
  // Called when the user allows notifications
  ClickedEnableNotificationsSuccess: Record<string, never>;
  // Called when the user denies notifications
  ClickedEnableNotificationsDenied: Record<string, never>;
  // Called when there was an error initiating or displaying the permission prompt
  ClickedEnableNotificationsError: Record<string, never>;
  // Called when we initiate the disable permission prompt from settings screen
  ClickedDisableNotifications: Record<string, never>;
  // Called when the user skips the notification permission prompt from settings screen
  ClickedSkipNotifications: Record<string, never>;
  // Called when we show the notification error alert
  ShowedNotificationErrorAlert: Record<string, never>;
  // Show notifications prompt for case escalation
  ShowedNotificationPromptForCaseEscalation: Record<string, never>;
  // Web to mobile modal shown when case is escalated
  WebToMobileModalShown: Record<string, never>;
  // Web to mobile modal dismissed by user
  WebToMobileModalDismissed: Record<string, never>;
  // Web to mobile modal auto-dismissed after timeout
  WebToMobileModalAutoDismissed: Record<string, never>;

  // Notification prompt buttons
  // Called when the user presses the allow button in the notification prompt
  ClickedAllowNotifications: Record<string, never>;
  // Called when the user presses the dismiss button in the notification prompt
  ClickedDismissNotifications: Record<string, never>;

  // Called when the user submits a HIPAA release
  StartHipaaRelease: Record<string, never>;
  SignHipaaRelease: { patientIsSelf: boolean };
  CompletedHipaaRelease: Record<string, never>;
  FailedHipaaRelease: Record<string, never>;
  FailedHipaaReleasePreview: Record<string, never>;
  SubmitHipaaRelease: Record<string, never>;
  HipaaReleaseSubscriberCheck: { subscriber: "YES" | "NO" };

  // HIPAA release v3 flow-engine funnel. Additive to the legacy Start/Sign/
  // Completed/Failed names above, which stay as dashboard join keys. Screen
  // events fire from onStateLoaded; the Form submitted outcome from onTransition.
  HipaaReleaseFormStepShown: { screenTag: string };
  HipaaReleaseSuccessStepShown: { screenTag: string };
  HipaaReleaseFormSubmittedOutcome: {
    outcomeTag: string;
    fromScreenTag: string;
  };
  HipaaReleaseFormRejectedOutcome: {
    outcomeTag: string;
    fromScreenTag: string;
    rejectedFieldCount: number;
    requiredMissingCount: number;
    notConformantCount: number;
    unrecognizedCodeCount: number;
    formMessageShown: "YES" | "NO";
  };

  // Chat
  CreateIssue:
    | {
        source:
          | "CASE_SCREEN"
          | "CASE_INTAKE_FLOW"
          | "BILL_DISPUTE"
          | "ACCOUNT_LINK_FAILURE";
      }
    | {
        source: "WELCOME_SCREEN";
        action:
          | "EXPLAIN_BENEFITS"
          | "REVIEW_BILLS"
          | "GET_STARTED"
          | "REVIEW_BILLS_NEED_HELP_WITH_SPECIFIC_BILL"
          | "HELP_WITH_BILL"
          | "UPLOAD_BILL_CTA";
      };
  SentMessage: { issueId: string };
  ChatFirstTokenLatency: {
    issueId: string;
    /** The issue's case type, so latency is segmentable without a warehouse join. */
    caseType?: string;
    /** Send tap → first streamed token rendered, in milliseconds. */
    timeToFirstTokenMs: number;
    /** Send tap → TURN_STARTED received, in milliseconds (transport-in portion). */
    timeToTurnStartMs: number;
  };
  OpenedIssue: { issueId: string; source: string };
  // Fires when the user taps "view all" under the capped "Your cases" home list.
  ViewAllCasesTapped: { source: CaseAnalyticsSource };
  // Fires when the user taps the Cases Home attention alert (≥1 blocked case).
  AttentionAlertTapped: { source: CaseAnalyticsSource; blockedCount: number };
  // Fires when a get-help preview chip answers in place instead of opening the case.
  GetHelpPresetTapped: { preset: string; replyId: GetHelpPromptKey };
  // Legacy realized-upload counter: fires *after* a selection, so it measures
  // completed picks rather than intent. Kept unchanged for dashboard
  // continuity; the UploadFunnel* events below are the funnel.
  ClickedUpload: { type: "FILE" | "GALLERY" | "CAMERA" };
  /**
   * The top of the upload funnel — the trigger tap on every surface (chat
   * paperclip, home CTA, post-escalation card, FA field, preview-retry tile).
   * The tap and the sheet render are the same synchronous moment, so this is
   * emitted from the shared sheet open rather than five separate buttons.
   * Mints `uploadSessionId`. On web there is no chooser sheet — the tap opens
   * the OS file dialog directly — so this and `UploadMethodSelected` (always
   * `FILE`) fire together, once per dialog open; a retry tap is a new session.
   *
   * Warehouse note: bill-intake drop-off dashboards must exclude
   * `entryPoint: FA_WIZARD` from every event in this funnel. FA uploads ride
   * the same sheet but answer a document request inside a form rather than
   * starting a bill — mixing them dilutes bill-intake drop-off.
   */
  UploadInitiated: UploadFunnelEventBase;
  // Fires on the method card *tap*, before the picker or scanner opens, so it
  // captures intent that `ClickedUpload` (post-selection) cannot.
  UploadMethodSelected: UploadFunnelEventBase & { method: UploadMethod };
  // One terminal per picker open. `SELECTED` with `fileCount: 0` is impossible
  // by construction — an empty return is `CANCELED`.
  UploadPickerReturned: UploadFunnelEventBase & {
    method: UploadMethod;
    outcome: UploadPickerOutcome;
    fileCount: number;
    totalSizeBytes?: number;
    durationMs: number;
    /**
     * Set whenever `outcome` is `ERROR`, and on `NO_RESPONSE` as
     * `ACTIVATION_LOST` when the browser reported no user activation at open.
     */
    errorType?: UploadErrorType;
  };
  // Sheet closed without any method being tapped — pure abandonment. Native
  // only: web has no sheet, so its abandonment signal is
  // `UploadPickerReturned: CANCELED` (or `NO_RESPONSE` for unreported ones).
  UploadSheetDismissed: UploadFunnelEventBase;
  UploadSubmitted: UploadFunnelEventBase & {
    method: UploadMethod;
    fileCount: number;
    /** Omitted when any file in the batch reported an unknown size. */
    totalSizeBytes?: number;
  };
  UploadSucceeded: UploadFunnelEventBase & {
    method: UploadMethod;
    fileCount: number;
    totalSizeBytes?: number;
    durationMs: number;
    connectionType?: UploadConnectionType;
  };
  /**
   * `durationMs` and `totalSizeBytes` are carried here as well as on success so
   * a slow upload that failed is distinguishable from an immediate rejection,
   * and so throughput (bytes ÷ ms) separates a failure on a poor connection
   * from a server-side one — the two have opposite fixes, and `errorType`
   * alone can't tell them apart. `connectionType` is the remaining signal:
   * cellular vs wifi vs offline, when the platform can report it.
   */
  UploadFailed: UploadFunnelEventBase & {
    method: UploadMethod;
    fileCount: number;
    totalSizeBytes?: number;
    durationMs: number;
    connectionType?: UploadConnectionType;
    stage?: UploadFailureStage;
    errorType: UploadErrorType;
  };
  /**
   * The third terminal: the app died between `UploadSubmitted` and an outcome.
   * Emitted at the next launch from the durable record the provider writes at
   * submit — without it a killed upload emits `UploadSubmitted` and then
   * nothing, the same unattributable gap the scan funnel had before
   * `DocumentScanAbandoned`. Native only: it needs a process that restarts,
   * which a closed web tab does not give us.
   *
   * `durationMs` is the last-known in-flight time, not time-to-death — JS stops
   * running when the process does, so it measures submit → the last stage
   * transition we managed to persist.
   */
  UploadAbandoned: UploadFunnelEventBase & {
    method: UploadMethod;
    fileCount: number;
    totalSizeBytes?: number;
    durationMs: number;
    connectionType?: UploadConnectionType;
    stage: UploadFailureStage;
    reason: UploadAbandonReason;
  };
  // Fires when the native Scanbot document scanner opens (camera path only).
  DocumentScanStarted: DocumentScanEventBase;
  // Fires per acknowledge-screen quality analysis; `consecutiveRejectCount`
  // counts sub-minimum results within the session and resets on accept.
  DocumentScanQualityResult: DocumentScanEventBase & {
    quality: DocumentScanQuality;
    accepted: boolean;
    /** The gate `accepted` was derived against — segmentable without LD. */
    minimumAcceptedQuality: DocumentScanQuality;
    consecutiveRejectCount: number;
    analysisIndex: number;
  };
  /**
   * Fires when the quality gate rejects an analyzed capture (the
   * `accepted: false` subset of DocumentScanQualityResult). Separate event
   * because LD experiment metrics match on event name only, and reject volume
   * per arm is part of the quality-experiment readout.
   */
  DocumentScanRejected: DocumentScanEventBase & {
    quality: DocumentScanQuality;
    minimumAcceptedQuality: DocumentScanQuality;
    consecutiveRejectCount: number;
    analysisIndex: number;
  };
  // Fires when the user retakes a page after a quality rejection.
  DocumentScanRetake: DocumentScanEventBase & {
    consecutiveRejectCount: number;
    analysisIndex: number;
  };
  // Fires when Scanbot returns pages successfully (before upload starts).
  DocumentScanCompleted: DocumentScanEventBase & {
    pageCount: number;
    durationMs: number;
    maxConsecutiveRejectCount: number;
  };
  /**
   * Fires alongside DocumentScanCompleted when the session hit ≥1 quality
   * reject — with DocumentScanCanceledAfterReject it splits reject→recovered
   * from reject→gave-up for the quality-experiment readout (LD metrics can't
   * filter on payload fields).
   */
  DocumentScanCompletedAfterReject: DocumentScanEventBase & {
    maxConsecutiveRejectCount: number;
  };
  // Fires when the user cancels out of Scanbot.
  DocumentScanCanceled: DocumentScanEventBase & {
    pageCount: number;
    durationMs: number;
    maxConsecutiveRejectCount: number;
  };
  // Fires alongside DocumentScanCanceled when the session hit ≥1 quality
  // reject — the reject→gave-up half of the recovery split.
  DocumentScanCanceledAfterReject: DocumentScanEventBase & {
    maxConsecutiveRejectCount: number;
  };
  // Fires when the scanner ends without usable images for a non-cancel reason.
  DocumentScanFailed: DocumentScanEventBase & {
    reason: "NO_IMAGES" | "UNKNOWN";
    durationMs: number;
    maxConsecutiveRejectCount: number;
  };
  /**
   * Fourth terminal outcome: the session went silent without a
   * Completed/Canceled/Failed event. Promotes the previously derived
   * absence-based signal to a first-class event so it can be a LaunchDarkly
   * metric and carry duration + the rejected-then-abandoned context
   * (`maxConsecutiveRejectCount` > 0). `elapsedMs` approximates active session
   * time in both cases so the two reasons stay comparable: Started →
   * foreground-exit for `BACKGROUNDED` (the grace wait is excluded), Started →
   * last quality result for `TERMINATED`.
   */
  DocumentScanAbandoned: DocumentScanEventBase & {
    reason: DocumentScanAbandonReason;
    maxConsecutiveRejectCount: number;
    analysisIndex: number;
    elapsedMs: number;
  };
  ClickedChatEvent: {
    type:
      | "DOCUMENT_UPLOADED"
      | "HIPAA_RELEASE_REQUESTED"
      | "TEMPLATE_RELEASE_REQUESTED"
      | "FINANCIAL_ASSISTANCE_FLOW";
  };
  HipaaReleaseSigned: { templateId: string; organizationSlug?: string };
  // Fires when the in-chat FA offer card renders — the funnel's top, so
  // offer→start conversion reads as
  // Started ÷ FinancialAssistanceOfferShown, joined on issueId.
  FinancialAssistanceOfferShown: { issueId: string };
  // Fires when a chat document ask renders, in every arm — the upload funnel's
  // denominator for the card experiment, so gate completion reads as
  // UploadSucceeded ÷ DocumentAskShown per `arm`, joined on issueId and
  // entryPoint. Control has no card, so an impression fired from the card
  // would leave the treatment arm with no comparison.
  DocumentAskShown: {
    issueId: string;
    eventId: string;
    entryPoint: DocumentScanEntryPoint;
    documentType: string;
    arm: DocumentRequestCardVariant;
  };
  // Fires when an active typed-chat prompt renders. Document asks use
  // DocumentAskShown instead. Answered ÷ Shown, joined on issueId + eventId.
  UserInputShown: {
    issueId: string;
    eventId: string;
    fieldKey: string;
    fieldType: string;
    skippable: boolean;
    sourceDocumentId?: string;
    flowId?: string;
  };
  // Fires when the user submits, skips, or picks and the client accepts it.
  // `answerMode` / `textShape` match the Datadog classifiers; no answer value.
  UserInputAnswered: {
    issueId: string;
    eventId: string;
    fieldKey: string;
    fieldType: string;
    answerMode: UserInputAnswerMode;
    textShape: UserInputTextShape;
    sourceDocumentId?: string;
    flowId?: string;
  };
  // A recovery notice rendered. Deduplicate observationId across reloads;
  // this measures client observation, not backend failure incidence.
  GuidedUploadRecoveryShown: {
    issueId: string;
    documentId: string;
    attemptId: string;
    observationId: string;
    stage: "FAILED" | "DEGRADED" | "RETAKE_ASKED";
  };
  // The member clicked a recovery action, before its request completes.
  GuidedUploadRecoveryAction: {
    issueId: string;
    documentId: string;
    action: "retry" | "continue_manually";
  };
  // Fires when the user accepts the in-chat FA offer card and opens the wizard.
  FinancialAssistanceApplicationStarted: FinancialAssistanceWizardEventBase;
  // Fires when the user reopens an in-progress FA application; `source` is the
  // affordance they used to reopen. Which affordance drives resumes reads as
  // Resumed(source:"banner") ÷ Resumed(source:any) — attribution among resumes,
  // not against banner impressions (the banner re-renders many times a session).
  FinancialAssistanceApplicationResumed: FinancialAssistanceWizardEventBase & {
    source: FAWizardOpenSource;
  };
  // Fires when the user skips the in-chat FA offer card.
  FinancialAssistanceApplicationSkipped: { issueId: string };
  // Fires when the FA wizard's final submit is accepted by the backend.
  // `sectionCount` is the visible-section total; `confirmationCount` is how many
  // prefilled values the applicant confirmed on the upfront prefill step.
  FinancialAssistanceApplicationSubmitted: FinancialAssistanceWizardEventBase & {
    sectionCount: number;
    confirmationCount: number;
  };
  // Fires once each time a wizard section is shown (screen entry, incl. the
  // first section on open/resume). `fieldKeys` are the section's form keys, so
  // drop-off is sliceable by field content ("steps with an SSN question")
  // rather than an opaque position — dynamic/provider forms make a bare index
  // meaningless. `index` is the section's rank among visible sections;
  // `totalVisibleSections` is the drop-off denominator. Reaching Review & sign
  // is just the StepShown whose `sectionKey` is the review section.
  // Bespoke rather than createFlowTracker: every dynamic section shares the
  // "Section" tag, which would collapse the tracker's per-tag event name.
  FinancialAssistanceWizardStepShown: FinancialAssistanceWizardEventBase & {
    sectionKey: string;
    fieldKeys: string[];
    index: number;
    totalVisibleSections: number;
  };
  // Fires when the upfront prefill step renders — the confirmation moment before
  // the form; `prefilledCount` is how many values the applicant must confirm or
  // reject on it. Pair with `FinancialAssistancePrefillResolved` for step
  // drop-off (shown ÷ resolved).
  FinancialAssistancePrefillStepShown: FinancialAssistanceWizardEventBase & {
    prefilledCount: number;
  };
  // Fires when the applicant closes the wizard (✕) mid-flow, before submit;
  // `sectionKey` is where they bailed.
  FinancialAssistanceWizardDismissed: FinancialAssistanceWizardEventBase & {
    sectionKey: string;
  };
  // Fires when the final submit is rejected by validation (client-side or the
  // backend); `fieldKeys` are the offending answer keys and `errorCodes` the
  // backend field codes when the rejection carried them.
  FinancialAssistanceSubmitBlocked: FinancialAssistanceWizardEventBase & {
    fieldKeys: string[];
    errorCodes: string[];
  };
  // Document-section upload lifecycle; `fileCount` is the batch size.
  FinancialAssistanceDocumentUploadStarted: FinancialAssistanceWizardEventBase & {
    fileCount: number;
  };
  FinancialAssistanceDocumentUploadSucceeded: FinancialAssistanceWizardEventBase & {
    fileCount: number;
  };
  FinancialAssistanceDocumentUploadFailed: FinancialAssistanceWizardEventBase & {
    fileCount: number;
  };
  // Fires when an address-search suggestion is picked and autofills the fields.
  FinancialAssistanceAddressAutofilled: { issueId: string };
  // Fires when the applicant opts into manual address entry over autocomplete.
  FinancialAssistanceAddressManualEntry: { issueId: string };
  // Fires when the applicant finishes the upfront prefill step, with the count
  // of values they confirmed (kept) vs rejected (blanked to re-enter).
  FinancialAssistancePrefillResolved: FinancialAssistanceWizardEventBase & {
    confirmed: number;
    rejected: number;
  };
  // Fires when the applicant taps Edit on a section from the review summary.
  FinancialAssistanceReviewSectionEdited: { issueId: string };
  DocumentUploadStarted: {
    source: "CASE_INPUT" | "DOCUMENT_UPLOADED_EVENT";
  };
  // Cases Home impression + guardrail events. HomeScreenViewed fires once per
  // mount, the first time the screen finishes loading.
  HomeScreenViewed: {
    // All open cases shown.
    casesShownCount: number;
    // Cases blocked on the user (action required).
    actionableCasesCount: number;
    // Cases with an unread staff message and no action required.
    unreadCasesCount: number;
    hasClosedWin: boolean;
  };
  // The resolved primary CTA slot + secondary tiles the user saw; enables hero CTR.
  HomeCtaZoneViewed: {
    primary:
      | "billHelp"
      | "billMonitoring"
      | "coverage"
      | "newClaims"
      | "closedCase"
      | "none";
    secondary: string[];
  };
  // Redesign only. Fires when the home load-error banner shows (a shell query failed).
  HomeLoadFailed: { failedQueries: string[] };
  // For tracking the order of pill prompts shown to the user
  PillPromptsShown: {
    pillPromptsOrder: string[];
    caseType: string;
  };

  // Insurance Linking Steps - Web Agent
  // NOTE: Use useAccountLinkAnalytics() hook to track these events.
  // The hook automatically adds linkCategory: 'INSURANCE' | 'EHR' based on the flow.
  WebAgentCredentials: { linkCategory?: "INSURANCE" | "EHR" };
  WebAgentMfaSelection: {
    preferredMethod: string | undefined;
    linkCategory?: "INSURANCE" | "EHR";
  };
  WebAgentSecurityQuestion: { linkCategory?: "INSURANCE" | "EHR" };
  WebAgentMfaCode: { linkCategory?: "INSURANCE" | "EHR" };
  WebAgentResendMfaCode: { linkCategory?: "INSURANCE" | "EHR" };
  WebAgentManagedMfaApproval: {
    approved: boolean;
    linkCategory?: "INSURANCE" | "EHR";
  };

  // Insurance Linking General
  // NOTE: Use useAccountLinkAnalytics() hook to track these events.
  // The hook automatically adds linkCategory: 'INSURANCE' | 'EHR' based on the flow.
  /** Should be fired any time we open the insurance link flow
   * source is where in the app the user was when they started the flow
   * so for example if they were on the home screen and clicked the button in
   * the empty bills widget it would be "home_screen_empty_bills_widget"
   */
  InsuranceLinkingStarted: {
    source: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  FinishedInsuranceLinking:
    | {
        linkType: Endpoint;
        status: "ERROR";
        errorCode: "UNKNOWN";
        linkCategory?: "INSURANCE" | "EHR";
        lastLinkStep: InsuranceLinkStep | "UNKNOWN";
      }
    | {
        linkType: Endpoint;
        status: "SUCCESS";
        linkCategory?: "INSURANCE" | "EHR";
      }
    | {
        linkType: Endpoint;
        status: "EXIT";
        linkCategory?: "INSURANCE" | "EHR";
      };
  InsuranceLinkAlternatePlan: { linkCategory?: "INSURANCE" | "EHR" };
  InsuranceLinkErrorStep: {
    step: InsuranceLinkPage;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceLinkBackStep: {
    initialRoute: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceLinkCloseStep: {
    initialRoute: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceLinkSkipped: {
    source: string;
    reason: "SKIP_BUTTON" | "CHAT_SKIP_BUTTON";
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceLinkExitSurveyOpened: { linkCategory?: "INSURANCE" | "EHR" };
  InsuranceLinkExitSurvey: {
    reason: string[];
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceLinkExitSurveyExtra: {
    details: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  ClickedConnectInsurance: {
    linkType: Endpoint;
    insuranceName: string | undefined | null;
    linkCategory?: "INSURANCE" | "EHR";
  };
  ClickedAddInsurance: {
    linkType: Endpoint;
    linkCategory?: "INSURANCE" | "EHR";
  };
  SkipInsuranceLinkOnboarding: {
    onboardingVersion?: OnboardingVersionTag;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceLinkOnboardingBackStep: {
    initialRoute: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  SelectedInsuranceType: { linkCategory?: "INSURANCE" | "EHR" };
  // When the user presses yes on the loginInfoReady screen confirming they have
  // their credentials ready
  InsuranceLoginInfoYesPressed: { linkCategory?: "INSURANCE" | "EHR" };
  // When get help sheet is opened on loginInfoReady screen
  InsuranceLoginHelpSheetOpened: {
    insuranceName: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceLoginHelpSheetLinkClicked: {
    insuranceName: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceHelpSheetEmailSupportClicked: {
    insuranceName: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceHelpSheetResetLoginInformationClicked: {
    insuranceName: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceHelpSheetRegisterAccountClicked: {
    insuranceName: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceMaxAttemptsReached: {
    insuranceName: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceMaxAttemptsResetPasswordClicked: {
    insuranceName: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceMaxAttemptsPortalClicked: {
    insuranceName: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceHelpSheetViewGuideClicked: {
    insuranceName: string;
    guideUrl: string;
    linkCategory?: "INSURANCE" | "EHR";
  };

  // Fake Door MMFA — tracks every user action through the experiment flow
  FakeDoorMmfaViewed: FakeDoorMmfaEventBase & {
    step: "success_linked" | "claim_monitoring" | "email_action" | "waitlist";
  };
  FakeDoorMmfaContinued: FakeDoorMmfaEventBase & {
    step: "success_linked" | "claim_monitoring" | "email_action";
  };
  FakeDoorMmfaSkipped: FakeDoorMmfaEventBase & {
    step: "claim_monitoring" | "email_action";
  };
  FakeDoorMmfaDismissed: FakeDoorMmfaEventBase & {
    step: "success_linked" | "claim_monitoring" | "email_action" | "waitlist";
  };
  FakeDoorMmfaCompleted: FakeDoorMmfaEventBase;

  // User created tracking application installs/signups
  UserCreated: { email: string };
  // User authenticated for the first time following a signup
  UserFirstAuthentication: { email: string | undefined };
  // Server-confirmed Terms/Privacy acceptance
  TermsAccepted: { termsVersion: string };

  // Mirror of a Singular event; fires even when delivery is suppressed for an
  // internal user (`skipForInternalUser`).
  SingularEvent: {
    event: SingularEventName;
    skipForInternalUser: boolean;
    args?: ApiObject;
  };

  // Hipaa Release Events
  HipaaReleasePersonMatched: {
    useMatchedPatient: boolean;
  };

  // Onboarding Events
  /**
   * Auth/redirect-stage progression on the authenticate screen ("loading" →
   * destination) — its only remaining emitters. In-flow screen progression
   * is covered by the StepShown/Outcome pattern events below.
   * @deprecated Every emit site is duplicated by an event at the same moment
   * (`EmailVerificationLinkError` / `AuthenticateError` /
   * `UserFirstAuthentication` / `SingularEvent{loginCompleted}` + the auto
   * page views); only the name-visible stage funnel is unique. Don't add
   * emitters — the authenticate-screen replacement is a planned follow-up.
   * (`OnboardingFlowStepBackward` is already gone, replaced by
   * `OnboardingBackOutcome`.)
   */
  OnboardingFlowStepForward: {
    from?: string;
    to: string;
    onboardingVersion?: OnboardingVersionTag;
  };

  /**
   * The postauth onboarding shell mounted with a resolved resume read — the
   * signed-in counterpart of `SignUpOnboardingFlowStarted`, whichever door
   * the user came through (forced resume, first-auth routing, direct visit).
   */
  OnboardingResumeStarted: {
    onboardingVersion: OnboardingVersionTag;
  };
  OnboardingBillCompletionArmAssigned: {
    variant: OnboardingBillCompletionDestination;
  };

  // Onboarding screens
  OnboardingValuePropsStepShown: OnboardingScreenEventBase;
  OnboardingIntentStepShown: OnboardingScreenEventBase;
  OnboardingNotificationsStepShown: OnboardingScreenEventBase;
  OnboardingCompleteStepShown: OnboardingScreenEventBase;
  OnboardingBillEducationStepShown: OnboardingScreenEventBase;
  OnboardingSaveEducationStepShown: OnboardingScreenEventBase;
  OnboardingFinancialAssistanceStepShown: OnboardingScreenEventBase;
  OnboardingHouseholdSizeStepShown: OnboardingScreenEventBase;
  OnboardingIncomeStepShown: OnboardingScreenEventBase;
  OnboardingFaResultStrongCaseStepShown: OnboardingScreenEventBase;
  OnboardingFaResultCatastrophicStepShown: OnboardingScreenEventBase;
  OnboardingProfileStepShown: OnboardingScreenEventBase;
  OnboardingInsuranceStepShown: OnboardingScreenEventBase;
  OnboardingPhoneStepShown: OnboardingScreenEventBase;

  // Onboarding shared outcomes — every screen's back / plain-continue funnels here
  OnboardingBackOutcome: OnboardingOutcomeEventBase;
  OnboardingContinueOutcome: OnboardingOutcomeEventBase;

  // Onboarding screen-specific outcomes
  OnboardingValuePropsSignUpOutcome: OnboardingOutcomeEventBase;
  OnboardingValuePropsLogInOutcome: OnboardingOutcomeEventBase;
  OnboardingIntentSelectedOutcome: OnboardingOutcomeEventBase & {
    intent: string;
  };
  OnboardingNotificationsConfiguredOutcome: OnboardingOutcomeEventBase & {
    enabled: boolean;
  };
  OnboardingNotificationsSkippedOutcome: OnboardingOutcomeEventBase;
  OnboardingHouseholdSizeSelectedOutcome: OnboardingOutcomeEventBase & {
    householdSize: number;
  };
  OnboardingIncomeCheckedOutcome: OnboardingOutcomeEventBase & {
    outcome: string;
  };
  OnboardingProfileSubmittedOutcome: OnboardingOutcomeEventBase;
  OnboardingPhoneSubmittedOutcome: OnboardingOutcomeEventBase;
  OnboardingPhoneSkippedOutcome: OnboardingOutcomeEventBase;
  OnboardingInsuranceSelectedOutcome: OnboardingOutcomeEventBase & {
    carrier: string;
    /** Null when the member typed a carrier the catalog search didn't list. */
    payerId: string | null;
  };
  OnboardingInsuranceUninsuredOutcome: OnboardingOutcomeEventBase;
  OnboardingInsuranceSkippedOutcome: OnboardingOutcomeEventBase;

  // Natera partner intake funnel — screens
  /** Web-only extras diagnose preload vs real visits and in-app browser context. */
  NateraIntakeNateraConfirmStepShown: NateraIntakeScreenEventBase & {
    visibilityState?: "visible" | "hidden" | "prerender";
    prerendering?: boolean;
    hasBeenActive?: boolean;
    innerWidth?: number;
    innerHeight?: number;
    inAppBrowser?: "facebook" | "instagram" | "threads" | "none";
  };
  NateraIntakeBillAmountStepShown: NateraIntakeScreenEventBase;
  NateraIntakePlaybookStepShown: NateraIntakeScreenEventBase;
  NateraIntakeBillCountStepShown: NateraIntakeScreenEventBase;
  NateraIntakeStressStepShown: NateraIntakeScreenEventBase;
  NateraIntakeFeelingStepShown: NateraIntakeScreenEventBase;
  NateraIntakePersonaValidationStepShown: NateraIntakeScreenEventBase;
  /** Express arm only: asked after sign-up. */
  NateraIntakeInsuranceStepShown: NateraIntakeScreenEventBase;
  NateraIntakeHouseholdStepShown: NateraIntakeScreenEventBase;
  NateraIntakeIncomeStepShown: NateraIntakeScreenEventBase;
  NateraIntakeAnalyzingStepShown: NateraIntakeScreenEventBase;
  NateraIntakeFaResultStepShown: NateraIntakeScreenEventBase;
  NateraIntakeStoryStepShown: NateraIntakeScreenEventBase;
  NateraIntakeSocialProofStepShown: NateraIntakeScreenEventBase;
  NateraIntakeAccountStepShown: NateraIntakeScreenEventBase;

  // Natera partner intake funnel — shared outcomes; every screen's back / plain-continue funnels here
  NateraIntakeBackOutcome: NateraIntakeOutcomeEventBase;
  NateraIntakeContinueOutcome: NateraIntakeOutcomeEventBase;

  // Natera partner intake funnel — screen-specific outcomes
  NateraIntakeNateraConfirmConfirmedOutcome: NateraIntakeOutcomeEventBase;
  /**
   * A lab bill that isn't Natera's. Answers the same landing questions and leaves
   * for regular onboarding at the validation screen; split out because its share
   * is what decides whether other labs ever earn a tailored funnel of their own.
   */
  NateraIntakeNateraConfirmOtherLabOutcome: NateraIntakeOutcomeEventBase;
  /** Bound for regular onboarding — traffic this campaign paid for and can't serve. */
  NateraIntakeNateraConfirmNotNateraOutcome: NateraIntakeOutcomeEventBase;
  NateraIntakeBillAmountSubmittedOutcome: NateraIntakeOutcomeEventBase & {
    billRangeFloorCents: number | null;
    billRangeCeilingCents: number | null;
  };
  /** The member moved past the amount without giving one. Paired against
   *  `...SubmittedOutcome` this is the screen's skip rate. */
  NateraIntakeBillAmountSkippedOutcome: NateraIntakeOutcomeEventBase;
  NateraIntakeBillCountSelectedOutcome: NateraIntakeOutcomeEventBase & {
    billCount: number;
  };
  NateraIntakeStressSelectedOutcome: NateraIntakeOutcomeEventBase & {
    stressLevel: number;
  };
  /** The persona-assigning moment: stress x feeling. The assigned persona rides
   *  `persona` on this event and every one after it. */
  NateraIntakeFeelingSelectedOutcome: NateraIntakeOutcomeEventBase & {
    feeling: string;
  };
  /** Express arm only: asked after sign-up. */
  NateraIntakeInsuranceSelectedOutcome: NateraIntakeOutcomeEventBase & {
    carrier: string;
    /** Null when the user typed a carrier the catalog search didn't list. */
    payerId: string | null;
  };
  NateraIntakeInsuranceUninsuredOutcome: NateraIntakeOutcomeEventBase;
  NateraIntakeInsuranceSkippedOutcome: NateraIntakeOutcomeEventBase;
  NateraIntakeHouseholdSelectedOutcome: NateraIntakeOutcomeEventBase & {
    householdSize: number;
  };
  /** Carries no figure and no band. The FA read one screen later is the income
   *  answer that matters, and it rides `faBranch` from there on. */
  NateraIntakeIncomeSubmittedOutcome: NateraIntakeOutcomeEventBase;
  /** "Prefer not to say" — recorded, and routes FA-not-pursued. */
  NateraIntakeIncomeDeclinedOutcome: NateraIntakeOutcomeEventBase;
  /** The FA-read-assigning moment; the read rides `faBranch` from here on. */
  NateraIntakeAnalyzingReadOutcome: NateraIntakeOutcomeEventBase;
  /** Re-answering income from the result screen — a signal the read landed wrong. */
  NateraIntakeFaResultReopenIncomeOutcome: NateraIntakeOutcomeEventBase;
  /** Express arm: once per quote, when the plan card's fate is known. */
  NateraIntakeQuoteInsuranceResolved: NateraIntakeRevision &
    NateraIntakeExpressArmed & {
      outcome: NateraIntakeQuoteInsuranceOutcome;
      elapsedMs: number;
      /** From the payer pick to the plan card coming back; null if it hadn't by the decision. */
      cardLandedMs: number | null;
    };
  /** Free text never ships; submitted-vs-skipped is the whole signal. */
  NateraIntakeStorySubmittedOutcome: NateraIntakeOutcomeEventBase;
  NateraIntakeStorySkippedOutcome: NateraIntakeOutcomeEventBase;
  /** The signup: the pre-auth conversion, and the last thing measurable before the
   *  member leaves for their inbox or their provider. `method` mirrors the shared
   *  `SubmitLogin` split. Not a flow transition — the screen routes out itself. */
  NateraIntakeAccountSubmittedOutcome: NateraIntakeOutcomeEventBase & {
    method: "EMAIL" | "OAUTH";
  };
  /** Without this, a blocked send and a member who never typed both end the
   *  session on `AccountStepShown`. */
  NateraIntakeAccountFailedOutcome: NateraIntakeOutcomeEventBase & {
    reason: "BOT_CONTROL" | "SEND_REJECTED" | "SEND_THREW";
  };

  /** Diagnoses whether taps register and which element was touched (first screen drop-off). */
  NateraIntakeFirstScreenTouch: {
    target: "card" | "heading" | "gap" | "other";
    cardValue?: "natera" | "other-lab" | "something-else";
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    movedPx: number;
    pressFired: boolean;
  };
  /** Diagnoses whether the first screen was seen and interacted with before the user left. */
  NateraIntakeFirstScreenDwell: {
    visibleMs: number;
    touched: boolean;
    pressFired: boolean;
  };
  /** Fired once, when the arm is first read; not for restored or unenrolled users. */
  NateraIntakeExpressArmAssigned: NateraIntakeRevision &
    Pick<NateraIntakeEventBase, "funnelVersion" | "landingEntry"> & {
      arm: NateraExpressVariant;
    };
  NateraIntakeLandingAnswersFromUrl: NateraIntakeRevision & {
    billSource: "natera" | "other-lab" | "something-else";
    stressLevel: number;
    feeling: string;
  };

  // Natera partner intake funnel — conversion + activation hub
  /** The only event carrying both the RudderStack identity and the issue id, so
   *  it stitches the anonymous half of the funnel to the case half. The case's
   *  `nateraIntake` marker, not this event, is what the cohort is keyed on. */
  NateraIntakeCaseCreated: NateraIntakeRevision &
    NateraIntakeExpressArmed & {
      issueId: string;
      funnelVersion: NateraIntakeVersionTag;
      /** False when the case already existed — a returning member re-landing. */
      created: boolean;
    };
  /** Authenticated with no case: a conversion the marker-based cohort can't see.
   *  Should be ~0; anything else is conversions vanishing after auth. */
  NateraIntakeCaseCreationFailed: NateraIntakeExpressArmed & {
    message: string;
  };
  /** The post-auth identity step, which runs before the case exists — hence no
   *  issueId, unlike the rest of this family. Shown only to a member with no
   *  identity on file, so the pair is the auth→hub drop-off. */
  NateraIntakeActivationIdentityShown: NateraIntakeRevision;
  NateraIntakeActivationIdentityRecorded: NateraIntakeRevision;
  /** The phone ask that follows identity. Submitted and Skipped are the exits;
   *  Failed is one rejected attempt, not an exit. The number never rides an event. */
  NateraIntakeActivationPhoneShown: NateraIntakeRevision;
  NateraIntakeActivationPhoneSubmitted: NateraIntakeRevision;
  NateraIntakeActivationPhoneSkipped: NateraIntakeRevision;
  NateraIntakeActivationPhoneFailed: NateraIntakeRevision & { message: string };
  /** Pressed past the advocate introduction; the drop against the hub view is
   *  what the introduction costs. */
  NateraIntakeActivationIntroContinued: NateraIntakeRevision & {
    issueId: string;
  };
  /** The activation denominator. Once per hub mount, including returning
   *  members; on chat, once per case-screen mount. The checklist is the hub's
   *  own read, so the item lists are absent on chat. */
  NateraIntakeActivationHubViewed: NateraIntakeRevision &
    NateraIntakeSurfaced & {
      issueId: string;
      requiredItems?: NateraIntakeActivationItem[];
      missingItems?: NateraIntakeActivationItem[];
      /** The card row was settled by the eligibility check, not an upload. */
      insuranceVerified?: boolean;
      /** The card row was settled by "I don't have insurance". */
      uninsuredDeclared?: boolean;
    };
  NateraIntakeActivationItemStarted: NateraIntakeRevision &
    NateraIntakeSurfaced & {
      issueId: string;
      item: NateraIntakeActivationItem;
    };
  /** Fires when the server's read stops listing the item as missing — completion
   *  is never assumed from the client returning out of a sub-flow. */
  NateraIntakeActivationItemCompleted: NateraIntakeRevision &
    NateraIntakeSurfaced & {
      issueId: string;
      item: NateraIntakeActivationItem;
      /** Completed by answering "I don't have income documents", not by an upload. */
      noDocuments?: boolean;
    };
  /** The coverage answer, in either direction — UNINSURED is the opt-out for a
   *  member with no card to send, INSURED is them taking it back. */
  NateraIntakeActivationInsuranceStatusDeclared: NateraIntakeRevision &
    NateraIntakeSurfaced & {
      issueId: string;
      status: InsuranceStatus;
    };
  /** The member pressed the hub's Submit — escalation is theirs to trigger,
   *  never a side effect of the checklist finishing. */
  NateraIntakeActivationSubmitted: NateraIntakeRevision &
    NateraIntakeSurfaced & { issueId: string };
  /** Server accepted the delayed-card fee acknowledgement. Snapshots are the
   *  stored terms, not caller-supplied. No card-conversion payload. */
  NateraIntakeFeeAcknowledged: NateraIntakeRevision &
    NateraIntakeSurfaced & {
      issueId: string;
      eventId: string;
      delayedCardRoute: string;
      feePercent: number;
      feeCapCents: number;
      feeWaived: boolean;
    };
  /** Server escalated the delayed-card hub after Submit. Distinct from the
   *  press (`Submitted`) and from card conversion. */
  NateraIntakeActivationCompleted: NateraIntakeRevision &
    NateraIntakeSurfaced & {
      issueId: string;
      delayedCardRoute: string;
    };
  /** The member asked to be reminded to finish the documents. `remindAt` is the
   *  instant their `choice` resolved to on their own clock, as ISO 8601. */
  NateraIntakeActivationReminderRequested: NateraIntakeRevision &
    NateraIntakeSurfaced & {
      issueId: string;
      choice: "tonight" | "tomorrowMorning" | "thisWeekend";
      remindAt: string;
    };
  /** A member stuck on an activation error screen. `issueId` is absent when the
   *  case itself never got created, which is what separates a failure between
   *  `NateraIntakeCaseCreated` and the hub view. */
  NateraIntakeActivationErrorShown: NateraIntakeRevision &
    NateraIntakeExpressArmed & {
      reason: NateraIntakeActivationErrorReason;
      issueId?: string;
    };
  /** Activation sent the member home: no answers to open a case from and no
   *  case to resume. */
  NateraIntakeActivationSkipped: NateraIntakeRevision;
  /** The funnel's terminal state. Server truth is the escalation task — on chat
   *  escalation happens server-side, so the client sees it on the next case
   *  view and this count is a floor. */
  NateraIntakeEscalated: NateraIntakeSurfaced & { issueId: string };

  // Bills
  OpenedBill: { billId: string };
  ClickedHighPricePill: {
    eobId: string;
    // Absent on the health-data source: enrichment surfaces a HIGH rating, not a percentage.
    percentageHigher?: number;
    comparisonCount: number;
    source?: "health-data";
  };

  // Feedback
  FeedbackProvided: { rating: number };
  /** Fired when the user sets the inline case-review star rating (saved on tap,
   *  before submit). `isUpdate` is false for the first rating, true when changing
   *  an already-set one. */
  FeedbackRatingSet: { issueId: string; rating: number; isUpdate: boolean };
  FeedbackDeepLink:
    | { result: "SUCCESS" }
    | { result: "ERROR"; error: "CASE_OPEN" };

  // Review
  /** Fired when the user initiates a review request */
  ReviewRequestInitiated: Record<string, never>;
  /** Fired when the user completes a review request */
  ReviewRequestCompleted: Record<string, never>;

  // Error
  ErrorScreenDisplayed: {
    screen: "NETWORK" | "BUG";
    errorType: string;
    message: string;
  };

  // Case Intake
  /** Should be fired any time we open the case intake flow
   * source is where in the app the user was when they started the flow
   * so for example if they were on the case list screen and clicked the button in
   * the empty case list it would be "cases_screen_empty_case_list"
   */
  CaseIntakeStarted: { source: string };
  CaseIntakeFlowStepForward: { to: string };
  CaseIntakeFlowStepBackward: { from: string; to: string };
  CaseIntakeFlowExit: { from: string | undefined };
  CaseIntakeCaseTypeSelection: { selection: string };
  CaseIntakeCaseSubTypeSelection: { type: string; selection: string };
  CaseIntakeSubmissionError: Record<string, never>;
  CaseIntakeSubmissionComplete: Record<string, never>;
  CaseIntakeSubmissionDuration: { duration: number };
  /** Fired when the floating create case button is pressed
   * The individual menu items are tracked separately with the CaseIntakeStarted event
   */
  FloatingCreateCaseButtonOpened: Record<string, never>;
  /** Fired when the user is prompted to select a plan for a case and they click
   * one of the options */
  PlanSelectedForPlanSelectionEvent: { planId: string };

  // Case close (user-initiated, from the case details screen)
  /** Fired when the user opens the close-case reason picker. */
  CaseCloseStarted: { issueId: string };
  /** Fired when the user confirms closing their own case, with the chosen reason. */
  CaseClosed: { issueId: string; reason: issue__UserClosureReason };

  // Web Onboarding Sign up
  // Screen 2/3 of the web sign up onboarding flow
  SignUpOnboardingUserNeedsCompleted: {
    selections: string[];
    onboardingVersion?: OnboardingVersionTag;
  };
  // Screen 3/3 of the web sign up onboarding flow
  SignUpOnboardingUsageScopeCompleted: {
    selections: string[];
    onboardingVersion?: OnboardingVersionTag;
  };
  // Fired once at every pre-auth signup entry (FFGA activate, /get-started
  // control and intake-routing arms) — the shared funnel denominator.
  SignUpOnboardingFlowStarted: {
    onboardingVersion?: OnboardingVersionTag;
  };

  // Intake routing: the pre-signup routing questions at /get-started. All
  // pre-auth.
  /**
   * The warehouse's exposure record for the first split: fires for every arm
   * at evaluation time, raw variant intact, so Snowflake can window on
   * `experiment-*` traffic without LD's event stream. `routingShown` is false
   * for the control arm; a member carrying `skipIntakeRouting` never reaches
   * the evaluation, so they produce no event rather than an unexposed one.
   */
  IntakeRoutingArmAssigned: {
    variant: IntakeRoutingVariant;
    routingShown: boolean;
  };
  /**
   * Exposure record for the second split; only a Natera-bill answer evaluates
   * it, so absence means unevaluated, not unexposed. Variant `onboarding` maps
   * to destination `signup`.
   */
  IntakeRoutingNateraArmAssigned: {
    variant: IntakeRoutingNateraDestination;
    destination: "natera-intake" | "signup";
  };
  IntakeRoutingBillQuestionStepShown: IntakeRoutingScreenEventBase;
  IntakeRoutingNateraBillQuestionStepShown: IntakeRoutingScreenEventBase;
  IntakeRoutingStressQuestionStepShown: IntakeRoutingScreenEventBase;
  IntakeRoutingFeelingQuestionStepShown: IntakeRoutingScreenEventBase;
  IntakeRoutingBillValidationStepShown: IntakeRoutingScreenEventBase;
  IntakeRoutingSaveValidationStepShown: IntakeRoutingScreenEventBase;
  IntakeRoutingBillQuestionSelectedOutcome: IntakeRoutingOutcomeEventBase & {
    hasBill: boolean;
  };
  IntakeRoutingNateraBillQuestionSelectedOutcome: IntakeRoutingOutcomeEventBase & {
    nateraBill: boolean;
  };
  IntakeRoutingStressQuestionSelectedOutcome: IntakeRoutingOutcomeEventBase & {
    stressLevel: number;
  };
  IntakeRoutingFeelingQuestionSelectedOutcome: IntakeRoutingOutcomeEventBase & {
    feeling: string;
  };
  IntakeRoutingBackOutcome: IntakeRoutingOutcomeEventBase;
  IntakeRoutingContinueOutcome: IntakeRoutingOutcomeEventBase;
  // The same entry's dead end: the invite error screen. `missing_token` means
  // the link carried no token at all (a broken link), `unresolved` that the
  // server would not resolve the one it carried (expired, revoked, mistyped).
  InviteActivationFailed: { reason: InviteActivationFailureReason };

  // Post-onboarding referral source tracking
  PostOnboardingReferralSource: {
    sources: string[];
    moreDetails?: string;
  };

  // Findings
  CreateIssueFromFindings: { title: string };
  OpenFindingsModal: { billId: string };

  // Bot Control : integrated with the (web-only) login page
  // Published each time the widget is presented to a user
  BotControlChallengeIssued: Record<string, never>;
  // Published when the challenge stops being invisible and asks the member to
  // act. `Issued` fires for every render, so it can't separate the silent pass
  // from the one that put a checkbox in front of someone mid-signup — this can.
  BotControlChallengeInteractive: Record<string, never>;
  // Published each time the challenge is solved
  // (whether automatically or with interactive checkbox)
  BotControlChallengeSolved: Record<string, never>;
  // Published each time the challenge expires
  // (5 minutes after the challenge is issued)
  BotControlChallengeExpired: Record<string, never>;
  // Published for any challenge that results in an error
  // https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
  BotControlChallengeError: { errorCode: string };

  /** Fired when the user clicks the continue button in the GetStartedSheet if
   * they have a benefits summary case */
  OpenedBenefitsSummary: Record<string, never>;

  /** Fired when the Review now button is tapped in the claims-review CTA */
  ClaimsReviewTapped: {
    action: "REVIEW_NOW" | "UPLOAD_BILL";
  };

  // Promo Code
  PromoCodeExpand: { issueId: string };
  PromoCodeApply: { issueId: string; code: string };
  PromoCodeCancel: { issueId: string };

  // Download App Sheet (web-only iOS prompt)
  /** Fired when the download app sheet is displayed to the user */
  DownloadAppSheetShown: Record<string, never>;
  /** Fired when the user clicks the download button in the sheet */
  DownloadAppSheetClicked: Record<string, never>;
  /** Fired when the user dismisses the download app sheet */
  DownloadAppSheetDismissed: Record<string, never>;

  // Cases panel & list analytics
  /** Fired when the cases slide panel is opened or closed */
  CasesPanelToggled: {
    action: PanelToggleAction;
    source: PanelSource;
  };
  /** Fired when a filter checkbox changes in the CaseListFilterPopover (v2 UI) */
  CasesFilterChanged: {
    filterType: CaseListFilterType;
    enabled: boolean;
    resultingFilters: CaseListFilters;
  };
  /** Fired when the back button is pressed on the case page (mobile web) */
  CaseScreenBackButtonPressed: Record<string, never>;
  /** Fired when the case details modal is opened */
  CaseDetailsOpened: { issueId: string };

  // Case lifecycle
  /** Fired when the user taps Reopen on a closed case. */
  CaseReopenRequested: { issueId: string };

  // Post-escalation card
  /** Fired when a post-escalation action is clicked */
  PostEscalationActionTapped: {
    issueId: string;
    actionType: string;
  };

  // Bill Pay v2 — funnel for the BillPayAgreementModal flow. Every event
  // extends BillPayEventBase. Outcomes (accept / decline) come from
  // `BillPayRequestedEvent.acceptedAt` directly — we don't mirror backend
  // state into client analytics.
  /** Fired when the "Start saving" CTA on the BillPayRequestedEvent task card is tapped. */
  BillPayClickedStart: BillPayEventBase;
  /** Fired when the user advances from one bill-pay agreement step to the next. */
  BillPayAgreementStepForward: BillPayEventBase & {
    from: BillPayStepId;
    to: BillPayStepId;
  };
  /** Fired when the user goes back to a previous bill-pay agreement step. */
  BillPayAgreementStepBack: BillPayEventBase & {
    from: BillPayStepId;
    to: BillPayStepId;
  };
  /** Fired when the bill-pay agreement modal closes before the user accepts. */
  BillPayAgreementDismissed: BillPayEventBase & { step: BillPayStepId };
  /** Fired when the user opens the payment-method editor inside the bill-pay flow. */
  BillPayClickedEditPaymentMethod: BillPayEventBase;
  /** Fired when the user toggles the savings-preference radio. Captures behavior
   *  mid-flow — the final value also rides on `BillPayClickedPreAuthorize`. */
  BillPayChangedSavingsPreference: BillPayEventBase & {
    value: "any" | "minimum";
  };
  /** Fired when the user picks a minimum-discount tile (only meaningful when
   *  savingsPreference === "minimum"). Logs each pick, including re-picks. */
  BillPayChangedMinimumDiscount: BillPayEventBase & { value: number };
  /** Fired when the user adds or removes an alternative-strategy option. */
  BillPayChangedAlternativeStrategy: BillPayEventBase & {
    value: AlternativeStrategy;
    action: "add" | "remove";
  };
  /** Fired when the user picks a success-fee tier (10/15/20%). */
  BillPayChangedSuccessFee: BillPayEventBase & { value: 10 | 15 | 20 };
  /** Fired when the user taps "Save" on the inline payment-method editor. */
  BillPayClickedSavePaymentMethod: BillPayEventBase;
  /** Fired when the user cancels the inline payment-method editor without saving. */
  BillPayClickedCancelPaymentMethodEdit: BillPayEventBase;
  /** Fired when the user expands the promo-code input inside the bill-pay flow. */
  BillPayClickedPromoCodeExpand: BillPayEventBase;
  /** Fired when the user taps "Apply" on the promo-code input inside the bill-pay flow. */
  BillPayClickedPromoCodeApply: BillPayEventBase & { code: string };
  /** Fired when the server returns a FormError on promo-code apply (typed
   *  success-response variant Apollo doesn't surface to Sentry). Transport
   *  errors are intentionally not logged here — those flow to Sentry already. */
  BillPayPromoCodeApplyFailed: BillPayEventBase & {
    code: string;
    reason: string;
  };
  /** Fired when the user removes an applied promo code inside the bill-pay flow. */
  BillPayClickedRemovePromoCode: BillPayEventBase;
  /** Fired when the user taps "Pre-authorize payment" on the success-fee step.
   *  Carries the final settled values for self-describing terminal analytics;
   *  use the `BillPayChanged…` events for behavior leading up to this point.
   *  `minimumDiscountPercent` is null when `savingsPreference === "any"` to
   *  distinguish "didn't pick a threshold" from a numeric threshold. */
  BillPayClickedPreAuthorize: BillPayEventBase & {
    successFeePercent: number;
    savingsPreference: "any" | "minimum";
    minimumDiscountPercent: number | null;
  };

  // Payment-preferences intake — funnel for the PaymentPreferencesRequestedEvent
  // takeover. Every event extends PaymentPrefsEventBase; outcomes (confirm /
  // decline) come from the event's confirmedAt/declinedAt, not client analytics.
  /** Fired when the intake takeover opens. `source` is the entry point that
   *  launched it, for cohort splits (chat event card vs post-escalation). */
  PaymentPrefsScreenViewed: PaymentPrefsEventBase & {
    caseType: issue__PaymentCaseType;
    source: PaymentPrefsSource;
  };
  /** Fired when the card first offers the question path, which it withholds on
   *  render. `reason` splits the two audiences so each reveal rule can be
   *  judged on its own conversion. */
  PaymentPrefsQuestionsRevealed: PaymentPrefsEventBase & {
    reason: PaymentPrefsQuestionsRevealReason;
  };
  /** Fired when the user starts the payment-question exchange from the card.
   *  The questions themselves are agent-driven from there, so the per-question
   *  breakdown comes from the conversation, not from client events. */
  PaymentPrefsQuestionsOpened: PaymentPrefsEventBase & {
    reason: PaymentPrefsQuestionsRevealReason;
  };
  /** Fired when the user advances to a later wizard step; `step` is the step
   *  now shown (2 or 3). Powers the intake funnel's step-drop analysis. */
  PaymentPrefsStepAdvanced: PaymentPrefsEventBase & { step: number };
  /** Fired when the user steps back to an earlier wizard step; `step` is the
   *  step now shown (1 or 2). Pairs with StepAdvanced to spot friction that
   *  sends users backwards. */
  PaymentPrefsStepBack: PaymentPrefsEventBase & { step: number };
  /** Fired when the takeover closes without confirming — the only abandonment
   *  signal, since declining is server-side and most users just leave. */
  PaymentPrefsDismissed: PaymentPrefsEventBase & { atStep: number };
  /** Fired when the user opts out of accept-any-savings into the max slider. */
  PaymentPrefsCustomMaxOpened: PaymentPrefsEventBase;
  /** Fired when the user settles on a max-single-charge value. `pctOfBill` is
   *  the value as a whole-number percent of the bill, omitted when the issue
   *  carries no bill amount. */
  PaymentPrefsMaxChargeSet: PaymentPrefsEventBase & {
    value: number;
    pctOfBill?: number;
  };
  /** Fired when the user switches the auto-pay radio. */
  PaymentPrefsAutoPayToggled: PaymentPrefsEventBase & { value: boolean };
  /** Fired when the auto-pay opt-out warning first shows for a session. */
  PaymentPrefsOptOutWarningShown: PaymentPrefsEventBase;
  /** Fired when the user toggles the payment-plan switch. */
  PaymentPrefsPlanToggled: PaymentPrefsEventBase & { value: boolean };
  /** Fired when the user sets a max monthly plan amount (integer cents). */
  PaymentPrefsPlanMonthlySet: PaymentPrefsEventBase & { value: number };
  /** Fired when the user expands the promo-code entry. */
  PaymentPrefsPromoExpanded: PaymentPrefsEventBase;
  /** Fired when the user taps to remove an applied promo code. Optimistic —
   *  fires on the tap, matching the funnel's other interaction events and the
   *  UI, which clears the pill regardless of the mutation result. */
  PaymentPrefsPromoRemoveTapped: PaymentPrefsEventBase;
  /** Fired when a promo apply resolves (Stripe preview display-only). `code` is
   *  the entered code (marketing code, not PII); `reason` carries the failure
   *  message on error so we can see which codes fail and why. */
  PaymentPrefsPromoApplied: PaymentPrefsEventBase & {
    result: "success" | "error";
    code: string;
    reason?: string;
  };
  /** Fired when the user toggles the provider-relationship switch (negotiation only). */
  PaymentPrefsProviderRelationshipToggled: PaymentPrefsEventBase & {
    value: issue__ProviderRelationship;
  };
  /** Fired when the user leaves a non-empty advocate note (no PII in payload). */
  PaymentPrefsNoteEntered: PaymentPrefsEventBase;
  /** Fired when the user confirms. Carries the settled non-PII values.
   *  Accept-any-savings sends no maxes at all — there is no ceiling to report.
   *  `setMax` is `!acceptAny`, kept alongside it so pre-acceptAny dashboards
   *  keyed on it keep working. `source` repeats ScreenViewed's rather than
   *  being joined to it — Amplitude can only segment an event by its own
   *  properties, so confirm rate per entry point needs it on both. */
  PaymentPrefsConfirmed: PaymentPrefsEventBase & {
    source: PaymentPrefsSource;
    /** Null on SIMPLIFIED — those asks were never shown. */
    acceptAny: boolean | null;
    maxSingleCharge?: number;
    maxSettle?: number;
    setMax: boolean | null;
    autoPayAuthorized: boolean | null;
    paymentPlanRequested: boolean | null;
    providerRelationship: issue__ProviderRelationship | null;
  };
  /** Fired when the user skips a fully-waived (fee cap 0) request from the
   *  event card, declining it server-side. */
  PaymentPrefsSkipped: PaymentPrefsEventBase;
  /** Fired when the skip's server-side decline fails. */
  PaymentPrefsSkipFailed: PaymentPrefsEventBase & { message: string };
  /** Fired when confirm fails (persist error, or the fallback re-auth of an
   *  expired hold is declined). */
  PaymentPrefsConfirmFailed: PaymentPrefsEventBase & { message: string };
  /** Fired when the step-1 "Authorize card" pre-auth fails; the user stays
   *  on step 1 to fix the card. */
  PaymentPrefsCardAuthFailed: PaymentPrefsEventBase & {
    message: string;
    /** PREAUTH_* extensions.code from the server, null for unclassified failures. */
    code: string | null;
  };
  /** Fired when the user opens the card-capture editor, or starts a wallet
   *  (`wallet` set). */
  PaymentPrefsCardCaptureStarted: PaymentPrefsWalletEventBase;
  /** Fired when the user taps Save in the card editor — the tap, not the
   *  outcome (see CardCaptureSucceeded/Failed), so stuck saves are visible. */
  PaymentPrefsCardSaveTapped: PaymentPrefsEventBase;
  /** Fired when the user cancels out of the card editor or wallet sheet. */
  PaymentPrefsCardEditCancelled: PaymentPrefsWalletEventBase;
  /** Fired when a card is saved and the profile reflects the new card. */
  PaymentPrefsCardCaptureSucceeded: PaymentPrefsWalletEventBase;
  /** Fired when card capture surfaces a Stripe/init error. */
  PaymentPrefsCardCaptureFailed: PaymentPrefsWalletEventBase;
  /** Fired after the card-collection hold is verified and persisted. */
  CardCollectionCaptureSucceeded: {
    issueId: string;
    eventId: string;
    method: CardCollectionMethod | "resumed";
  };
  /** Fired when the card-collection hold cannot be verified. */
  CardCollectionCaptureFailed: {
    issueId: string;
    eventId: string;
    method: CardCollectionMethod | "resumed";
  };
  /** Fired once per mount of the pending request card, after wallet
   *  availability resolves. `wallet` names the offered wallet, or null. */
  CardCollectionOffered: {
    issueId: string;
    eventId: string;
    walletAvailable: boolean;
    wallet: CardCollectionWallet | null;
  };
  /** Fired when the member chooses the manual card-entry path. */
  CardCollectionCardEntryStarted: { issueId: string; eventId: string };
  CardCollectionWalletStarted: {
    issueId: string;
    eventId: string;
    wallet: CardCollectionWallet;
  };
  CardCollectionWalletCancelled: {
    issueId: string;
    eventId: string;
    wallet: CardCollectionWallet;
  };
  /** Fired when a wallet confirm fails for a non-cancel reason. */
  CardCollectionWalletFailed: {
    issueId: string;
    eventId: string;
    wallet: CardCollectionWallet;
  };

  // Failed-charge alert — the two recovery paths offered on the chat card.
  /** User taps through to update their payment method. `issueId` is absent
   *  when the tap lands before the case has loaded; the button navigates
   *  regardless, so the event fires either way. */
  PaymentChargeFailedUpdatePaymentTapped: { issueId?: string };
  /** Each step of updating the card in chat from the charge-failed card;
   *  `succeeded` fires once the new card is the member's default. */
  PaymentChargeFailedCardSave: {
    issueId: string;
    step: CardSaveStep;
    wallet?: CardCollectionWallet;
  };
  /** User signals the charge can be retried (moved-funds path) — fires on the
   *  tap, before the mutation resolves. */
  PaymentChargeFailedRetryReadyTapped: { issueId: string };
};

export type AttributionTraits = {
  // UTM parameters
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  utm_id?: string;
  utm_source_platform?: string; // sometimes included by Google Ads
  utm_creative_format?: string; // creative type
  utm_marketing_tactic?: string; // extended tracking

  // Self-attributing network (SAN) click IDs
  gclid?: string; // Google Ads
  wpkwn?: string; // Google Ad words
  gbraid?: string; // Google iOS app conversions
  wbraid?: string; // Google iOS web-to-app conversions
  fbclid?: string; // Facebook
  ttclid?: string; // TikTok
  twclid?: string; // Twitter
  li_fat_id?: string; // LinkedIn
  ScCid?: string; // Snapchat
  irclickid?: string; // Impact
  msclkid?: string; // Microsoft Ads
  yclid?: string; // Yandex Ads
  vcid?: string; // Verizon / Yahoo Ads

  // Apple Search Ads
  asa?: string;
  asa_campaign?: string;
  asa_adgroup?: string;
  asa_keyword?: string;
  // Apple Search Ads V2
  campaignId?: string;
  adgroupId?: string;
  keywordId?: string;
  creativeSetId?: string;

  // Complete Meta
  fbadid?: string;
  fbadname?: string;
  fbadsetid?: string;
  fbadsetname?: string;
  fbcampaignid?: string;
  fbcampaignname?: string;

  // Complete Instagram (Old)
  igshid?: string;
  ig_rid?: string;
  ig_mid?: string;

  // Other common attribution parameters
  referrer?: string;
  ref?: string;
  source?: string;
  ref_id?: string; // affiliate/referral code
  pid?: string; // affiliate partner ID
  cid?: string; // campaign/customer ID

  // Which partner landing page sent the user. Shared, public marketing config
  // printed on every CTA — unlike `inviteToken`, which is a bearer secret and
  // stays credential-stripped.
  defaultInviteToken?: string;

  // Store when attribution was first captured
  attribution_timestamp?: string;
};

export type UserTraits = {
  name?: string;
  email?: string;
  isInternal?: boolean;
  userSegmentFeatures?: string[];
};

export type ScreenViewProperties = Record<string, string | string[]>;

// Auth landings carry live credentials in their URL params (magic-link or
// OAuth session token; OTP code + email pair). There is no single shared sink —
// web and native each have their own trackScreenView implementation — so both
// must strip them before logs, RudderStack, or LaunchDarkly see the properties.
const CREDENTIAL_PARAMS: ReadonlySet<string> = new Set([
  "token",
  "code",
  "email",
  "inviteToken",
  // Not `defaultInviteToken`: a default landing-page token is public marketing
  // config, and attribution reads it.
]);

export function stripCredentialParams(
  properties: ScreenViewProperties,
): ScreenViewProperties {
  return Object.fromEntries(
    Object.entries(properties).filter(([key]) => !CREDENTIAL_PARAMS.has(key)),
  );
}

// Define events to be sent to third-party tracking software listed in the
// trackingConfig. Custom events can use specific configurations if necessary.
export const eventTrackingConfig: {
  [E in keyof EventTypes]?: ApiOptions;
} = {
  UserCreated: trackingConfig, // Mapped to Signed Up properties: {email} preserve in transformation
  CreateIssue: trackingConfig, // Mapped to View Content (properties: { title } is purged in transformation
  // Add more events with custom configs as needed
};

// Insurance-related event names for use with useAccountLinkAnalytics hook
export type InsuranceEventNames =
  | "WebAgentCredentials"
  | "WebAgentMfaSelection"
  | "WebAgentSecurityQuestion"
  | "WebAgentMfaCode"
  | "WebAgentResendMfaCode"
  | "WebAgentManagedMfaApproval"
  | "InsuranceLinkingStarted"
  | "FinishedInsuranceLinking"
  | "InsuranceLinkAlternatePlan"
  | "InsuranceLinkErrorStep"
  | "InsuranceLinkBackStep"
  | "InsuranceLinkCloseStep"
  | "InsuranceLinkSkipped"
  | "InsuranceLinkExitSurveyOpened"
  | "InsuranceLinkExitSurvey"
  | "InsuranceLinkExitSurveyExtra"
  | "ClickedConnectInsurance"
  | "ClickedAddInsurance"
  | "SkipInsuranceLinkOnboarding"
  | "InsuranceLinkOnboardingBackStep"
  | "SelectedInsuranceType"
  | "InsuranceLoginInfoYesPressed"
  | "InsuranceLoginHelpSheetOpened"
  | "InsuranceLoginHelpSheetLinkClicked"
  | "InsuranceHelpSheetEmailSupportClicked"
  | "InsuranceHelpSheetResetLoginInformationClicked"
  | "InsuranceHelpSheetRegisterAccountClicked"
  | "InsuranceMaxAttemptsReached"
  | "InsuranceMaxAttemptsResetPasswordClicked"
  | "InsuranceMaxAttemptsPortalClicked"
  | "InsuranceHelpSheetViewGuideClicked"
  | "FakeDoorMmfaViewed"
  | "FakeDoorMmfaContinued"
  | "FakeDoorMmfaSkipped"
  | "FakeDoorMmfaDismissed"
  | "FakeDoorMmfaCompleted";

export type TrackEventOptions = {
  /**
   * The numeric value LaunchDarkly reads for a value metric. LD takes it from
   * the event's `metricValue`, never from a property inside the payload, so a
   * numeric metric reports nothing unless the number is passed here as well.
   * Rudder keeps using the payload property.
   */
  metricValue?: number;
};

export interface AnalyticsServiceInterface {
  initialize(config: AnalyticsConfig): Promise<void>;
  /**
   * Resolves once `initialize` has settled, reporting whether the SDK is
   * actually ready to accept events (`true`) or setup failed (`false`).
   * Callers that emit at startup await this so their event isn't silently
   * dropped by a `trackEvent` that ran before — or despite a failed — setup.
   */
  whenInitialized(): Promise<boolean>;
  trackEvent<E extends keyof EventTypes>(
    eventName: E,
    attributes: EventTypes[E],
    options?: TrackEventOptions,
  ): void;
  identifyUser(userId: string, traits: UserTraits): void;
  identifyAnonymousUser(traits: AttributionTraits): void;
  resetUser(): void;
  trackScreenView(screenName: string, properties: ScreenViewProperties): void;
  setAnonymousId(anonymousId: string): void;
  getAnonymousId(): string | undefined;
}
